import './BidvTransferResultModal.scss';
import React from 'react';
import { PopupWraper, Loading } from 'components';
import { Button } from 'modules';
import { Icon } from 'components/icon';
import { SubmitTransferData } from 'services/openapi';
import { useGetBidvTransferStatusQuery } from 'services';

interface BidvTransferResultModalProps {
  submitData: SubmitTransferData;
  onClose: () => void;
}

type StatusConfig = {
  label: string;
  statusClass: string;
  heroClass: string;
  iconColor: string;
  icon: React.ReactNode;
  description: string;
  isPending: boolean;
};

const getStatusConfig = (status?: string): StatusConfig => {
  const key = (status ?? '').toUpperCase();
  switch (key) {
    case 'THÀNH CÔNG':
    case 'SUCCESS':
      return {
        label: 'Thành công',
        statusClass: 'status-success',
        heroClass: 'hero--success',
        iconColor: '#4ade80',
        icon: <Icon.Checked size={56} />,
        description: 'Giao dịch đã được xử lý thành công.',
        isPending: false,
      };
    case 'THẤT BẠI':
    case 'FAILED':
      return {
        label: 'Thất bại',
        statusClass: 'status-failed',
        heroClass: 'hero--failed',
        iconColor: '#f87171',
        icon: <Icon.Error size={56} />,
        description: 'Giao dịch không thể hoàn tất. Vui lòng kiểm tra lại.',
        isPending: false,
      };
    case 'ĐANG XỬ LÝ':
    case 'PENDING':
    case 'PROCESSING':
      return {
        label: 'Đang xử lý',
        statusClass: 'status-pending',
        heroClass: 'hero--pending',
        iconColor: '#fbbf24',
        icon: <Icon.Reload size={56} />,
        description: 'Giao dịch đang được xử lý. Vui lòng kiểm tra lại sau.',
        isPending: true,
      };
    default:
      return {
        label: status || 'Không xác định',
        statusClass: 'status-unknown',
        heroClass: 'hero--unknown',
        iconColor: '#94a3b8',
        icon: <Icon.Info size={56} />,
        description: 'Không xác định được trạng thái giao dịch.',
        isPending: false,
      };
  }
};

const formatAmount = (amount: number) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(
    amount,
  );

const BidvTransferResultModal: React.FC<BidvTransferResultModalProps> = ({
  submitData,
  onClose,
}) => {
  const {
    data: statusData,
    isFetching,
    refetch,
  } = useGetBidvTransferStatusQuery(submitData.transactionCode, false);

  const currentStatus = statusData?.data?.status ?? submitData.status;
  const currentBeneficiaryName =
    statusData?.data?.beneficiaryName ?? submitData.beneficiaryName;
  const currentBeneficiaryAccount =
    statusData?.data?.beneficiaryAccount ?? submitData.beneficiaryAccount;
  const currentAmount = statusData?.data?.amount ?? submitData.amount;
  const remark = statusData?.data?.remark;
  const bidvErrorDesc = statusData?.data?.bidvErrorDesc;

  const {
    label,
    statusClass,
    heroClass,
    iconColor,
    icon,
    description,
    isPending,
  } = getStatusConfig(currentStatus);

  return (
    <PopupWraper center title='Kết quả giao dịch' onClose={onClose}>
      <Loading isLoading={isFetching}>
        <div className='bidv-transfer-result'>
          {/* Hero status section */}
          <div className={`bidv-transfer-result__hero ${heroClass}`}>
            <div
              className={`bidv-transfer-result__hero-icon ${heroClass === 'hero--pending' ? 'spinning' : ''}`}
              style={{ color: iconColor }}
            >
              {icon}
            </div>
            <span className={`bidv-transfer-result__badge ${statusClass}`}>
              {label}
            </span>
            <p className='bidv-transfer-result__hero-desc'>{description}</p>
          </div>

          {/* Info rows */}
          <div className='bidv-transfer-result__body'>
            <div className='bidv-transfer-result__row'>
              <span className='bidv-transfer-result__label'>Mã giao dịch</span>
              <span className='bidv-transfer-result__value mono'>
                {submitData.transactionCode}
              </span>
            </div>

            <div className='bidv-transfer-result__row'>
              <span className='bidv-transfer-result__label'>Số tiền</span>
              <span className='bidv-transfer-result__value amount'>
                {formatAmount(currentAmount)}
              </span>
            </div>

            <div className='bidv-transfer-result__row'>
              <span className='bidv-transfer-result__label'>
                Tên người nhận
              </span>
              <span className='bidv-transfer-result__value'>
                {currentBeneficiaryName}
              </span>
            </div>

            <div className='bidv-transfer-result__row'>
              <span className='bidv-transfer-result__label'>Số tài khoản</span>
              <span className='bidv-transfer-result__value mono'>
                {currentBeneficiaryAccount}
              </span>
            </div>

            {remark && (
              <div className='bidv-transfer-result__row'>
                <span className='bidv-transfer-result__label'>Nội dung</span>
                <span className='bidv-transfer-result__value'>{remark}</span>
              </div>
            )}

            {bidvErrorDesc && (
              <div className='bidv-transfer-result__row bidv-transfer-result__row--error'>
                <span className='bidv-transfer-result__label'>
                  Chi tiết lỗi
                </span>
                <span className='bidv-transfer-result__value error'>
                  {bidvErrorDesc}
                </span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className='bidv-transfer-result__actions'>
            {isPending && (
              <Button
                label={isFetching ? 'Đang kiểm tra...' : 'Kiểm tra trạng thái'}
                buttonType='primary'
                disabled={isFetching}
                onClick={() => refetch()}
              />
            )}
            <Button
              label='Đóng'
              buttonType='primary-outline'
              onClick={onClose}
            />
          </div>
        </div>
      </Loading>
    </PopupWraper>
  );
};

export default BidvTransferResultModal;
